'use client'
import { type ReactNode, useMemo } from 'react'
import { FeatureFlagsContext } from '@/features/shared/context/FeatureFlagsContext/FeatureFlagsContext'
import { defaultFlags } from '@/features/shared/services/FeatureFlags/constants'
import { featureFlagsQueryOptions } from '@/features/shared/services/FeatureFlags/queryOptions'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { createPassiveQueryStore } from '@/features/shared/utils/dataFetching/createPassiveQueryStore'

const useCachedFeatureFlags = createPassiveQueryStore(featureFlagsQueryOptions)

/**
 * Reads whatever flags a nested FeatureFlagsContextProvider has cached, without fetching.
 * Unlike a disabled useQuery it never creates the query, which would make a
 * HydrationBoundary below defer that query's server data to an effect, so SSR never saw it.
 */
export const CachedFeatureFlagsContextProvider = ({ children }: { children: ReactNode }) => {
  const { userId } = useSession()
  const data = useCachedFeatureFlags({ userId })

  const value = useMemo(() => {
    return {
      flags: data || defaultFlags,
      status: data ? ('success' as const) : ('pending' as const),
    }
  }, [data])

  return <FeatureFlagsContext value={value}>{children}</FeatureFlagsContext>
}
